require('./caption.scss');

export default class Caption {
    _node = null;
    _textNode = null;
    _cues = [];
    _currentCue = null;
    _media = null;
    _enabled = false;
    _hasWordTimings = false;

    constructor(wrapper) {
        this._wrapper = wrapper;
        this._init();
    }

    _init() {
        const node = document.createElement('div');
        node.classList.add('narration-caption');

        const text = document.createElement('div');
        text.classList.add('narration-caption-text');
        node.appendChild(text);

        this._node = node;
        this._textNode = text;
        // Append to body so it's centered on the full viewport
        document.body.appendChild(node);
    }

    async load(captionData) {
        this._cues = [];
        this._currentCue = null;
        this._hasWordTimings = false;
        this._textNode.innerHTML = '';
        this._textNode.classList.remove('visible');

        try {
            let text;
            if (typeof captionData === 'string' && captionData.match(/^(https?:\/\/|\/)/)) {
                const res = await fetch(captionData);
                text = await res.text();
            } else if (typeof captionData === 'string') {
                text = captionData;
            }

            if (text) {
                text = text.trim();
                if (text.startsWith('{')) {
                    const data = JSON.parse(text);
                    this._cues = data.segments || [];
                    this._hasWordTimings = this._cues.some(c => c.words && c.words.length > 0);
                } else {
                    this._cues = this._parseVTT(text);
                }
            }
            console.log('[Caption] Loaded', this._cues.length, 'cues, wordTimings:', this._hasWordTimings);
        } catch(e) {
            console.log('Caption load failed:', e);
        }
    }

    _parseVTT(text) {
        const cues = [];
        const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const blocks = normalized.split('\n\n');

        for (const block of blocks) {
            const match = block.match(
                /(?:(\d{2}):)?(\d{2}):(\d{2})[.,](\d{3})\s*-->\s*(?:(\d{2}):)?(\d{2}):(\d{2})[.,](\d{3})\n([\s\S]*)/
            );
            if (match) {
                const start = (parseInt(match[1] || '0') * 3600) +
                              (parseInt(match[2]) * 60) +
                              parseInt(match[3]) +
                              (parseInt(match[4]) / 1000);
                const end = (parseInt(match[5] || '0') * 3600) +
                            (parseInt(match[6]) * 60) +
                            parseInt(match[7]) +
                            (parseInt(match[8]) / 1000);
                cues.push({ start, end, text: match[9].trim().replace(/<[^>]+>/g, '') });
            }
        }
        return cues;
    }

    attach(mediaElement) {
        this.detach();
        this._media = mediaElement;
        this._media.addEventListener('timeupdate', this._onTimeUpdate);
    }

    detach() {
        if (this._media) {
            this._media.removeEventListener('timeupdate', this._onTimeUpdate);
            this._media = null;
        }
        this._currentCue = null;
        this._textNode.innerHTML = '';
        this._textNode.classList.remove('visible');
    }

    _onTimeUpdate = () => {
        if (!this._enabled || !this._media) return;

        const time = this._media.currentTime;
        const cue = this._cues.find(c => time >= c.start && time <= c.end) || null;

        if (cue !== this._currentCue) {
            if (cue) {
                this._currentCue = cue;
                this._renderCue(cue);
                this._textNode.classList.add('visible');
            }
            // Keep last cue visible in gaps — only hide when media ends or detach
        }

        // Update word highlighting within current cue
        if (cue && this._hasWordTimings && cue.words) {
            this._highlightWord(cue, time);
        }
    }

    _renderCue(cue) {
        if (this._hasWordTimings && cue.words) {
            this._textNode.innerHTML = cue.words.map((w, i) =>
                `<span class="caption-word" data-idx="${i}">${w.word}</span>`
            ).join(' ');
        } else {
            this._textNode.textContent = cue.text;
        }
    }

    _highlightWord(cue, time) {
        const words = this._textNode.querySelectorAll('.caption-word');
        for (let i = 0; i < cue.words.length; i++) {
            const w = cue.words[i];
            const el = words[i];
            if (!el) continue;
            if (time >= w.start && time <= w.end) {
                el.classList.add('caption-word--active');
            } else {
                el.classList.remove('caption-word--active');
            }
        }
    }

    toggle() {
        this._enabled = !this._enabled;
        if (!this._enabled) {
            this._currentCue = null;
            this._textNode.innerHTML = '';
            this._textNode.classList.remove('visible');
        } else {
            // Force re-render of current cue immediately
            this._currentCue = null;
            this._onTimeUpdate();
        }
        return this._enabled;
    }

    isEnabled() {
        return this._enabled;
    }

    hasCues() {
        return this._cues.length > 0;
    }

    show() {
        this._node.classList.remove('narration-caption--hidden');
    }

    hide() {
        this._node.classList.add('narration-caption--hidden');
    }

    destroy() {
        this.detach();
        if (this._node && this._node.parentNode) {
            this._node.parentNode.removeChild(this._node);
        }
    }
}
